import { useMemo, useState } from 'react';
import styled from 'styled-components';

import AccessGuard from 'components/shared/AccessGuard';
import CircularProgress from 'components/shared/CircularProgress';
import { useProfile } from 'context/profile';
import useChatMessages from 'hooks/useChatMessages';
import useMarkReadOnView from 'hooks/useMarkReadOnView';
import useSendMessage from 'hooks/useSendMessage';
import { PERMISSIONS } from 'models/Profile';
import { ChatConversation, ChatDraft, emptyChatDraft, isMentionNotVisibleError } from 'services/api/chat';
import { taggableUsersOnChatConversation } from 'services/api/taggableUsers';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT } from 'styles/theme/textStyles';
import { BodySmall, H3 } from 'styles/typography';

import ChannelEmptyState from './ChannelEmptyState';
import Composer from './Composer';
import MessageList from './MessageList';

type Props = {
  conversation: ChatConversation;
  // Multi-facility referral → the composer offers "Send to all facilities" (OLIO-2836).
  canBroadcast: boolean;
  // Per-conversation draft, owned by ChatTab so switching channels preserves unsent text/tags.
  draft: ChatDraft;
  onDraftChange: (value: ChatDraft) => void;
};

// One channel's pane: the message list (or empty state) above a plain composer. Messages
// and drafts are keyed by conversation, so switching channels swaps both. There is no
// read-only state: terminal referrals stay writable (QA PIQ 4 ruling — coordination
// continues after completion), and users without the send permission get no composer at
// all (QA PIQ 5; the API's create gate is the backstop).
export default function ChannelView({ conversation, canBroadcast, draft, onDraftChange }: Props) {
  const { profile } = useProfile();
  const { messages, isLoading, isError, hasOlder, isFetchingOlder, fetchOlder } = useChatMessages(conversation.id);
  const { sendMessage, isSending } = useSendMessage(conversation.id);

  // Local to this channel's pane and reset per channel (ChannelView is keyed by conversation),
  // so a broadcast is an explicit, one-shot choice — never a sticky mode across channels.
  const [broadcast, setBroadcast] = useState(false);
  // The per-recipient mention 422, shown inline in the composer (draft kept) instead of a toast.
  // Cleared whenever the draft changes or a send succeeds.
  const [mentionError, setMentionError] = useState<string | null>(null);

  const handleDraftChange = (value: ChatDraft) => {
    if (mentionError) setMentionError(null);
    onDraftChange(value);
  };

  // "Seen" means the newest message is actually IN THE VIEWPORT (OLIO-2835): a message
  // appended below the fold while the reader is scrolled up has not been seen, so the
  // cursor must not pass it — MessageList reports bottom-visibility and shows its "New
  // messages" pill instead. The mark fires once the reader returns to the bottom.
  const [newestVisible, setNewestVisible] = useState(false);
  const newestRenderedAt = messages.length ? messages[messages.length - 1].createdAt : undefined;
  useMarkReadOnView(conversation.id, newestRenderedAt, !isLoading && newestVisible);

  const handleSend = () => {
    if (!draft.plaintext.trim()) return;
    // Mentions ride along for the per-recipient server check; `broadcast` only when the
    // toggle is offered (guards a stale flag if the channel set narrows mid-session).
    sendMessage(
      {
        text: draft.text,
        plaintext: draft.plaintext,
        mentions: draft.mentions,
        broadcast: canBroadcast && broadcast,
      },
      {
        onSuccess: () => {
          onDraftChange(emptyChatDraft);
          setBroadcast(false);
          setMentionError(null);
        },
        // A rejected recipient keeps the draft and explains inline; other failures fall through
        // to useSendMessage's toast.
        onError: (error) => {
          if (isMentionNotVisibleError(error)) setMentionError('That person can’t see this conversation.');
        },
      }
    );
  };

  // Memoized so an inline object doesn't churn the taggable-users query key each render.
  const taggableUserResource = useMemo(() => taggableUsersOnChatConversation(conversation.id), [conversation.id]);

  return (
    <View data-testid='chat-channel-view'>
      {/* Shown for every referral, single facility included (OLIO-3116) — the divider beneath it
          reads as the chat body's own header band, matching the Figma single- and multi-SNF
          comps alike. */}
      <Header>
        <ViewingLabel>Viewing Chat For</ViewingLabel>
        <FacilityName>{conversation.audience.name}</FacilityName>
      </Header>

      {isLoading ? (
        <Centered>
          <CircularProgress />
        </Centered>
      ) : isError ? (
        // Before the empty state: a failed fetch must not masquerade as a fresh thread
        // (the channel may have a full history).
        <Centered>
          <Muted data-testid='chat-messages-error'>
            We couldn’t load this conversation’s messages. Refresh to try again.
          </Muted>
        </Centered>
      ) : messages.length === 0 ? (
        <ChannelEmptyState facility={conversation.audience.name} />
      ) : (
        <MessageList
          messages={messages}
          currentUserId={profile.id}
          hasOlder={hasOlder}
          isFetchingOlder={isFetchingOlder}
          onLoadOlder={fetchOlder}
          onNewestVisibleChange={setNewestVisible}
        />
      )}

      <AccessGuard accessor={PERMISSIONS.chatMessageCreate}>
        <Composer
          values={draft}
          onChange={handleDraftChange}
          onSend={handleSend}
          isSending={isSending}
          taggableUserResource={taggableUserResource}
          canBroadcast={canBroadcast}
          broadcast={broadcast}
          onBroadcastChange={setBroadcast}
          mentionError={mentionError}
        />
      </AccessGuard>
    </View>
  );
}

const View = styled.div`
  flex: 1;
  min-width: 0;
  /* min-height 0: let the internal message-list scroll win over content height when the
     rail stacks above in the narrow (column) layout. */
  min-height: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

// Bleeds past View's own 24px padding on both sides and the top, so the divider below the
// facility name spans the pane's true edges (Figma) rather than stopping at the text's inset —
// the negative margin cancels View's padding here, and this re-adds its own to keep the text
// aligned where it was.
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: -24px -24px 0;
  padding: 24px 24px 16px;
  border-bottom: 1px solid ${colors.black10};
`;

const ViewingLabel = styled(BodySmall)`
  color: ${colors.black50};
`;

const FacilityName = styled(H3)`
  color: ${colors.black100};
  /* One step below H3's medium (500) per design review of the Figma comp. */
  font-weight: ${FONT_WEIGHT};
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;
